<template>
  <div class="support-page">
    <!-- Support List View -->
    <div v-if="!route.params.id" class="support-list-view">
      <!-- Header -->
       
      <div class="support-header">
        <h1 class="support-title">{{ t('supportIndex.list.title') }}</h1>
        <v-btn 
          color="#EA5545" 
          @click="router.push({ name: 'help' })"
          class="create-btn"
        >
          <v-icon left class="mr-2">mdi-plus</v-icon>
          {{ t('supportIndex.list.newTicket') }}
        </v-btn>
      </div>

      <!-- AI Search Section -->
   

      <!-- Loading State -->
      <div v-if="isLoading" class="state-block" data-state="loading">
        <v-progress-circular indeterminate color="#EA5545" size="64"></v-progress-circular>
        <p class="state-text">{{ t('supportIndex.list.loading') }}</p>
      </div>

      <!-- Support Tickets List -->
      <div v-else-if="supportTickets.length > 0" class="tickets-grid">
        <v-card 
          v-for="ticket in supportTickets" 
          :key="ticket.sup_id"
          class="ticket-card"
          @click="viewTicket(ticket.sup_id)"
        >
          <v-card-text class="ticket-card-content">
            <!-- Left Column: Main Content -->
            <div class="ticket-left-column">
              <div class="ticket-id-row">
                <span class="ticket-id">#{{ ticket.sup_id }}</span>
                <v-badge
                  v-if="ticket.unread_count > 0"
                  :content="ticket.unread_count"
                  color="#EA5545"
                  inline
                  class="unread-badge ml-2"
                >
                  <v-icon size="18" color="#EA5545">mdi-message-badge</v-icon>
                </v-badge>
              </div>
              
              <h3 class="ticket-title-text">{{ ticket.sup_title }}</h3>
              
              <div v-if="ticket.prod_name" class="ticket-product">
                <v-icon size="14" color="#EA5545" class="mr-1">mdi-package</v-icon>
                <span>{{ ticket.prod_name }}</span>
              </div>
              
              <p class="ticket-text">{{ truncateText(ticket.sup_text, 150) }}</p>
            </div>
            
            <!-- Right Column: Status & Info -->
            <div class="ticket-right-column">
              <div class="ticket-status-section">
                <div class="status-label">{{ t('supportIndex.list.statusLabel') }}</div>
                <v-chip 
                  :color="getStatusColor(ticket.sup_status)" 
                  size="small"
                  class="status-chip-large"
                >
                  {{ getStatusText(ticket.sup_status) }}
                </v-chip>
              </div>
              
              <v-divider class="my-3"></v-divider>
              
              <div class="ticket-info-section">
                <div class="info-item">
                  <v-icon size="16" color="#666" class="mr-2">mdi-calendar</v-icon>
                  <div class="info-content">
                    <div class="info-label">{{ t('supportIndex.list.info.created') }}</div>
                    <div class="info-value">{{ formatDate(ticket.sup_created_date) }}</div>
                  </div>
                </div>
                
                <div v-if="ticket.sup_admin_readed" class="info-item">
                  <v-icon size="16" color="#4caf50" class="mr-2">mdi-check-all</v-icon>
                  <div class="info-content">
                      <div class="info-label">{{ t('supportIndex.list.info.adminStatus') }}</div>
                      <div class="info-value">{{ t('supportIndex.list.info.read') }}</div>
                  </div>
                </div>
                
                <div v-if="ticket.unread_count > 0" class="info-item highlight">
                  <v-icon size="16" color="#EA5545" class="mr-2">mdi-message-alert</v-icon>
                  <div class="info-content">
                      <div class="info-label">{{ t('supportIndex.list.info.newMessages') }}</div>
                    <div class="info-value">{{ ticket.unread_count }}</div>
                  </div>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </div>

      <!-- Empty State -->
      <div v-else class="state-block" data-state="empty">
        <v-icon size="80" color="#ccc">mdi-ticket-outline</v-icon>
        <h3 class="state-title">{{ t('supportIndex.list.emptyTitle') }}</h3>
        <p class="state-text">{{ t('supportIndex.list.emptyText') }}</p>
        <v-btn 
          color="#EA5545" 
          @click="router.push({ name: 'help' })"
          class="mt-4"
        >
          {{ t('supportIndex.list.emptyButton') }}
        </v-btn>
      </div>
    </div>

    <!-- Support Detail View -->
    <div v-else class="support-detail-view">
      <p v-if="currentTicketContractName" class="contract-breadcrumb">
        <span class="breadcrumb-label">{{ t('common.labels.contractBreadcrumbLabel') }} :</span>
        <router-link
          v-if="currentTicketContractLink"
          class="breadcrumb-value"
          :to="currentTicketContractLink"
        >
          {{ currentTicketContractName }}
        </router-link>
        <span v-else class="breadcrumb-value">
          {{ currentTicketContractName }}
        </span>
      </p>

      <div v-if="isLoading" class="state-block" data-state="loading">
        <v-progress-circular indeterminate color="#EA5545" size="64"></v-progress-circular>
      </div>

      <v-card v-else-if="currentTicket" class="detail-card">
        <v-card-text>
          <div class="detail-header">
            <div>
              <div class="ticket-id-large">#{{ currentTicket.sup_id }}</div>
              <h2 class="ticket-title-large">{{ currentTicket.sup_title }}</h2>
              <div v-if="currentTicket.prod_name" class="ticket-product-large">
                <v-icon size="18" color="#EA5545" class="mr-2">mdi-package-variant</v-icon>
                <span>{{ currentTicket.prod_name }}</span>
              </div>
            </div>
            <div class="header-actions">
              <v-chip 
                :color="getStatusColor(currentTicket.sup_status)" 
                size="large"
              >
                {{ getStatusText(currentTicket.sup_status) }}
              </v-chip>
              
              <v-btn
                v-if="currentTicket.sup_status != 2"
                color="grey"
                variant="outlined"
                @click="closeTicket"
                size="small"
                class="ml-2"
              >
                <v-icon left size="18">mdi-check</v-icon>
                {{ t('supportIndex.detail.buttons.close') }}
              </v-btn>
              
              <v-btn
                v-if="currentTicket.sup_status == 2"
                color="#EA5545"
                variant="outlined"
                @click="reopenTicket"
                size="small"
                class="ml-2"
              >
                <v-icon left size="18">mdi-refresh</v-icon>
                {{ t('supportIndex.detail.buttons.reopen') }}
              </v-btn>
            </div>
          </div>

          <div class="ticket-info-grid">
            <div class="info-item">
              <div class="info-label">{{ t('supportIndex.detail.info.created') }}</div>
              <div class="info-value">{{ formatDate(currentTicket.sup_created_date) }}</div>
            </div>
            <div class="info-item" v-if="currentTicket.prod_name">
              <div class="info-label">{{ t('supportIndex.detail.info.product') }}</div>
              <div class="info-value product-value">
                <v-icon size="16" color="#EA5545" class="mr-1">mdi-package</v-icon>
                {{ currentTicket.prod_name }}
              </div>
            </div>
            <div class="info-item">
              <div class="info-label">{{ t('supportIndex.detail.info.type') }}</div>
              <div class="info-value">{{ getTypeText(currentTicket.sup_type) }}</div>
            </div>
            <div class="info-item">
              <div class="info-label">{{ t('supportIndex.detail.info.adminStatus') }}</div>
              <div class="info-value">
                <span v-if="currentTicket.sup_admin_readed">
                  {{ t('supportIndex.detail.info.readOn', { date: formatDate(currentTicket.sup_admin_readed_date) }) }}
                </span>
                <span v-else class="unread">{{ t('supportIndex.detail.info.unread') }}</span>
              </div>
            </div>
          </div>

          <v-divider class="my-6"></v-divider>

          <div class="ticket-content">
            <h3 class="content-title">{{ t('supportIndex.detail.sections.description') }}</h3>
            <p class="content-text">{{ currentTicket.sup_text }}</p>
          </div>

          <div
            v-if="currentTicket.brain_request"
            class="brain-summary-card"
          >
            <div class="brain-summary-header">
              <div class="brain-icon">
                <v-icon size="20" color="white">mdi-brain</v-icon>
              </div>
              <div>
                <p class="brain-eyebrow">
                  {{ t('supportIndex.detail.brain.eyebrow') }}
                </p>
                <h3 class="brain-title">
                  {{ t('supportIndex.detail.brain.title') }}
                </h3>
              </div>
            </div>
            <div class="brain-body">
              <div>
                <label>{{ t('supportIndex.detail.brain.question') }}</label>
                <p>{{ currentTicket.brain_request.breq_prompt }}</p>
              </div>
              <div v-if="currentTicket.brain_request.breq_result">
                <label>{{ t('supportIndex.detail.brain.answer') }}</label>
                <p>{{ currentTicket.brain_request.breq_result }}</p>
              </div>
            </div>
          </div>

          <div v-if="currentTicket.sup_ext_url" class="external-link">
            <v-icon left color="#EA5545">mdi-link</v-icon>
            <a :href="currentTicket.sup_ext_url" target="_blank" class="link-text">
              {{ currentTicket.sup_ext_url }}
            </a>
          </div>

          <v-divider class="my-6"></v-divider>

          <!-- File Upload Section -->
          <div class="upload-section">
            <h3 class="content-title">{{ t('supportIndex.detail.sections.attachments') }}</h3>
            
            <div class="upload-area">
              <input 
                ref="fileInput"
                type="file" 
                multiple 
                @change="handleFileSelect"
                style="display: none"
              />
              
              <v-btn 
                color="#EA5545" 
                @click="$refs.fileInput.click()"
                :disabled="isUploading"
                class="upload-btn"
              >
                <v-icon left>mdi-paperclip</v-icon>
                {{ t('supportIndex.detail.upload.selectFiles') }}
              </v-btn>
              
              <span v-if="selectedFiles.length > 0" class="file-count">
                <template v-if="selectedFiles.length === 1">
                  {{ selectedFiles[0].name }}
                </template>
                <template v-else>
                  {{ t('supportIndex.detail.upload.filesSelected', { count: selectedFiles.length }) }}
                </template>
              </span>
            </div>

            <!-- Selected Files Preview -->
            <div v-if="selectedFiles.length > 0" class="selected-files">
              <div v-for="(file, index) in selectedFiles" :key="index" class="file-item">
                <v-icon color="#666" size="20">mdi-file</v-icon>
                <span class="file-name">{{ file.name }}</span>
                <span class="file-size">{{ formatFileSize(file.size) }}</span>
                <v-btn 
                  icon 
                  size="small" 
                  variant="text"
                  @click="removeFile(index)"
                  :disabled="isUploading"
                >
                  <v-icon size="18">mdi-close</v-icon>
                </v-btn>
              </div>

              <v-btn 
                color="#EA5545" 
                @click="uploadFiles"
                :loading="isUploading"
                :disabled="selectedFiles.length === 0"
                class="mt-4"
              >
                <v-icon left>mdi-upload</v-icon>
                {{ t('supportIndex.detail.upload.uploadButton') }}
              </v-btn>
            </div>

            <!-- Upload Progress -->
            <div v-if="isUploading" class="upload-progress">
              <v-progress-linear 
                color="#EA5545" 
                indeterminate
              ></v-progress-linear>
              <p class="progress-text">{{ t('supportIndex.detail.upload.uploading') }}</p>
            </div>

            <!-- Upload Results -->
            <div v-if="uploadResult" class="upload-result">
              <div v-if="uploadResult.successful_uploads.length > 0" class="success-uploads">
                <v-icon color="success" class="mr-2">mdi-check-circle</v-icon>
                <span>
                  {{ t('supportIndex.detail.upload.successPrefix') }}
                  {{ uploadResult.successful_uploads.join(', ') }}
                </span>
              </div>
              <div v-if="uploadResult.failed_uploads.length > 0" class="failed-uploads">
                <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
                <span>
                  {{ t('supportIndex.detail.upload.failedPrefix') }}
                  {{ uploadResult.failed_uploads.join(', ') }}
                </span>
              </div>
            </div>

            <!-- Uploaded Files List (nur User-Dateien, keine Admin-Dateien) -->
            <div v-if="userFiles.length > 0" class="uploaded-files-section">
              <h4 class="uploaded-files-title">{{ t('supportIndex.detail.upload.uploadedFilesTitle') }}</h4>
              <div class="uploaded-files-list">
                <div v-for="file in userFiles" :key="file.file_id" class="uploaded-file-item">
                  <div class="uploaded-file-content" @click="openUploadedFile(file)">
                    <v-icon :color="getFileIconColor(file.file_ext)" size="20">{{ getFileIcon(file.file_ext) }}</v-icon>
                    <span class="uploaded-file-name">{{ file.file_name }}</span>
                    <span class="uploaded-file-date">{{ formatDate(file.file_created_date) }}</span>
                  </div>
                  <v-btn 
                    icon 
                    size="small" 
                    variant="text"
                    color="error"
                    @click="confirmDeleteFile(file)"
                  >
                    <v-icon size="18">mdi-delete</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>
          </div>

          <v-divider class="my-6"></v-divider>

          <!-- Support Conversation -->
          <SupportConversation :supportId="currentTicket.sup_id"  :last_read_date="currentTicket.last_read_date" />
        </v-card-text>
      </v-card>
    </div>

    <!-- File Preview Dialog -->
    <v-dialog v-model="showFilePreviewDialog" max-width="900" scrollable>
      <v-card>
        <v-card-title class="file-dialog-title">
          <span>{{ previewFile?.file_name }}</span>
          <v-btn icon @click="showFilePreviewDialog = false" variant="text">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="file-dialog-content">
          <div v-if="previewFile" class="file-preview-container">
            <!-- Image Preview -->
            <div v-if="isImageFile(previewFile.file_ext)" class="image-preview">
              <img :src="getFileUrl(previewFile)" :alt="previewFile.file_name" />
            </div>
            
            <!-- PDF Preview -->
            <div v-else-if="previewFile.file_ext === 'pdf'" class="pdf-preview">
              <iframe :src="getFileUrl(previewFile)" width="100%" height="600px"></iframe>
            </div>
            
            <!-- Other Files -->
            <div v-else class="file-download-info">
              <v-icon size="80" :color="getFileIconColor(previewFile.file_ext)">
                {{ getFileIcon(previewFile.file_ext) }}
              </v-icon>
              <h3>{{ previewFile.file_name }}</h3>
              <p class="file-type-text">{{ previewFile.file_ext.toUpperCase() }} File</p>
              <v-btn 
                color="#EA5545" 
                :href="getFileUrl(previewFile)" 
                target="_blank"
                download
                class="mt-4"
              >
                <v-icon left>mdi-download</v-icon>
                Download File
              </v-btn>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { inject, ref, onMounted, watch, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import SupportConversation from '@/components/objects/SupportConversation.vue'
import useTranslations from '@/composables/useTranslations'
import { CLEARMENT_TITLE, CLEARMENT_DESCRIPTION } from '@/constants/documentHead'

const router = useRouter()
const route = useRoute()
const baseCalls = inject('baseCalls')
const errorStore = inject('errorStore')
const successStore = inject('successStore')
const userStore = inject('userStore')
const loaderStore = inject('loaderStore')
const updateDocumentHead = inject('updateDocumentHead')
updateDocumentHead?.(CLEARMENT_TITLE, CLEARMENT_DESCRIPTION)
const { t, languageStore } = useTranslations()
const currentLocale = computed(() =>
  languageStore?.currentLanguage?.value === 'de' ? 'de-DE' : 'en-US'
)

// State
const supportTickets = ref([])
const currentTicket = ref(null)
const isLoading = ref(false)
// File Upload State
const selectedFiles = ref([])
const isUploading = ref(false)
const uploadResult = ref(null)
const fileInput = ref(null)
const showFilePreviewDialog = ref(false)
const previewFile = ref(null)

const ticketTypes = computed(() => [
  { label: t('supportIndex.ticketTypes.general'), value: 0 },
  { label: t('supportIndex.ticketTypes.technical'), value: 1 },
  { label: t('supportIndex.ticketTypes.billing'), value: 2 },
  { label: t('supportIndex.ticketTypes.feature'), value: 3 }
])

// Computed: nur User-Dateien (file_is_admin = 0) für "Uploaded Files"
const userFiles = computed(() => {
  if (!currentTicket.value || !currentTicket.value.files) return []
  return currentTicket.value.files.filter(f => (f.file_is_admin ?? 0) === 0 || f.file_is_admin === '0')
})

const currentTicketContractName = computed(() => {
  if (!currentTicket.value) return ''
  return (
    currentTicket.value.prod_name ||
    `#${currentTicket.value.sup_product_id || currentTicket.value.sup_id}`
  )
})

const currentTicketContractLink = computed(() => {
  const contractId = currentTicket.value?.sup_product_id || currentTicket.value?.myp_product_id
  if (!contractId) {
    return null
  }
  return {
    name: 'vertrag',
    params: { id: contractId }
  }
})

// Helper Functions
function getStatusColor(status) {
  const colors = {
    0: 'orange',
    1: 'green',
    2: 'grey',
    3: 'red'
  }
  return colors[status] || 'grey'
}

function getStatusText(status) {
  const texts = {
    0: 'supportIndex.status.open',
    1: 'supportIndex.status.resolved',
    2: 'supportIndex.status.closed',
    3: 'supportIndex.status.urgent'
  }
  const key = texts[status] || 'supportIndex.status.unknown'
  return t(key)
}

function getTypeText(type) {
  const ticket = ticketTypes.value.find(tType => tType.value === type)
  return ticket ? ticket.label : t('supportIndex.ticketTypes.general')
}

function formatDate(dateString) {
  if (!dateString || dateString === '1973-01-01 00:00:00') return t('common.labels.notAvailable')
  const date = new Date(dateString)
  const locale = currentLocale.value || 'en-US'
  return date.toLocaleDateString(locale, { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function truncateText(text, maxLength) {
  if (!text) return ''
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

// API Functions
async function loadAllTickets() {
  isLoading.value = true
  try {
    const response = await baseCalls.clearmentPost({
      action: 'getAllMySupports'
    })
    
    if (response.data.success && response.data.supports) {
      supportTickets.value = response.data.supports
    }
  } catch (err) {
    console.error('Error loading tickets:', err)
    errorStore.setError(true, t('supportIndex.messages.loadTicketsError'))
  } finally {
    isLoading.value = false
  }
}

async function loadTicket(id) {
  isLoading.value = true
  try {
    const payload = {
      action: 'getsupport',
      sup_id: id
    }
    if (route.params.token) {
      payload.token = route.params.token
    }

    const response = await baseCalls.clearmentPost({
      ...payload
    })
    
    if (response.data.success && response.data.support) {
      currentTicket.value = response.data.support
    } else {
      const message = response.data?.message
      if (message === 'LOGIN_REQUIRED') {
        userStore.setLoginView?.(true)
        return
      }

      const errorKey =
        message === 'ACCESS_DENIED'
          ? 'supportIndex.messages.accessDenied'
          : 'supportIndex.messages.ticketNotFound'

      errorStore.setError(true, t(errorKey))

      if (message !== 'ACCESS_DENIED') {
        router.push('/support')
      }
    }
  } catch (err) {
    console.error('Error loading ticket:', err)
    errorStore.setError(true, t('supportIndex.messages.loadTicketError'))
    router.push('/support')
  } finally {
    isLoading.value = false
  }
}

function viewTicket(id) {
  router.push(`/support/${id}`)
}

async function closeTicket() {
  if (!currentTicket.value) return
  
  try {
    const response = await baseCalls.clearmentPost({
      action: 'updatesupportstatus',
      sup_id: currentTicket.value.sup_id,
      sup_status: 2
    })
    
    if (response.data.success) {
      successStore.setSuccess(
        true,
        t('supportIndex.messages.closeSuccessTitle'),
        t('supportIndex.messages.closeSuccessBody')
      )
      currentTicket.value.sup_status = 2
    } else {
      errorStore.setError(
        true,
        response.data.message || t('supportIndex.messages.closeFailed')
      )
    }
  } catch (err) {
    console.error('Error closing ticket:', err)
    errorStore.setError(true, t('supportIndex.messages.closeException'))
  }
}

async function reopenTicket() {
  if (!currentTicket.value) return
  
  try {
    const response = await baseCalls.clearmentPost({
      action: 'updatesupportstatus',
      sup_id: currentTicket.value.sup_id,
      sup_status: 0
    })
    
    if (response.data.success) {
      successStore.setSuccess(
        true,
        t('supportIndex.messages.reopenSuccessTitle'),
        t('supportIndex.messages.reopenSuccessBody')
      )
      currentTicket.value.sup_status = 0
    } else {
      errorStore.setError(
        true,
        response.data.message || t('supportIndex.messages.reopenFailed')
      )
    }
  } catch (err) {
    console.error('Error reopening ticket:', err)
    errorStore.setError(true, t('supportIndex.messages.reopenException'))
  }
}

function confirmDeleteFile(file) {
  const confirmMessage = t('supportIndex.messages.deleteConfirm', { file: file.file_name })
  if (window.confirm(confirmMessage)) {
    deleteFile(file.file_id)
  }
}

async function deleteFile(fileId) {
  try {
    const response = await baseCalls.clearmentPost({
      action: 'deletefile',
      file_id: fileId
    })
    
    if (response.data.success) {
      successStore.setSuccess(
        true,
        t('supportIndex.messages.deleteSuccessTitle'),
        t('supportIndex.messages.deleteSuccessBody')
      )
      
      // Remove file from current ticket's files array
      if (currentTicket.value && currentTicket.value.files) {
        currentTicket.value.files = currentTicket.value.files.filter(f => f.file_id != fileId)
      }
    } else {
      errorStore.setError(
        true,
        response.data.message || t('supportIndex.messages.deleteFailed')
      )
    }
  } catch (err) {
    console.error('Error deleting file:', err)
    errorStore.setError(true, t('supportIndex.messages.deleteException'))
  }
}

function getFileIcon(ext) {
  const icons = {
    pdf: 'mdi-file-pdf-box',
    doc: 'mdi-file-word',
    docx: 'mdi-file-word',
    xls: 'mdi-file-excel',
    xlsx: 'mdi-file-excel',
    ppt: 'mdi-file-powerpoint',
    pptx: 'mdi-file-powerpoint',
    txt: 'mdi-file-document',
    zip: 'mdi-zip-box',
    rar: 'mdi-zip-box',
    jpg: 'mdi-file-image',
    jpeg: 'mdi-file-image',
    png: 'mdi-file-image',
    gif: 'mdi-file-image',
    mp4: 'mdi-file-video',
    mp3: 'mdi-file-music',
  }
  return icons[ext.toLowerCase()] || 'mdi-file'
}

function getFileIconColor(ext) {
  const colors = {
    pdf: '#d32f2f',
    doc: '#1976d2',
    docx: '#1976d2',
    xls: '#388e3c',
    xlsx: '#388e3c',
    ppt: '#f57c00',
    pptx: '#f57c00',
    jpg: '#7b1fa2',
    jpeg: '#7b1fa2',
    png: '#7b1fa2',
    gif: '#7b1fa2',
    zip: '#616161',
    rar: '#616161',
  }
  return colors[ext.toLowerCase()] || '#666'
}

function openUploadedFile(file) {
  previewFile.value = file
  showFilePreviewDialog.value = true
}

function getFileUrl(file) {
  return `/getfile/${file.file_hash}`
}

function isImageFile(ext) {
  const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp']
  return imageExts.includes(ext.toLowerCase())
}

// File Upload Functions
function handleFileSelect(event) {
  const files = Array.from(event.target.files)
  selectedFiles.value = files
  uploadResult.value = null
}

function removeFile(index) {
  selectedFiles.value.splice(index, 1)
  if (selectedFiles.value.length === 0 && fileInput.value) {
    fileInput.value.value = ''
  }
}

function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
}

async function uploadFiles() {
  if (selectedFiles.value.length === 0 || !currentTicket.value) return

  isUploading.value = true
  uploadResult.value = null

  try {
    const formData = new FormData()
    
    // Add all files to FormData
    selectedFiles.value.forEach(file => {
      formData.append('files[]', file)
    })
    
    // Add support ticket ID
    formData.append('file_support_id', currentTicket.value.sup_id)

    const response = await baseCalls.clearmentUpload(formData)

    if (response.data.success) {
      uploadResult.value = response.data
      successStore.setSuccess(
        true,
        t('supportIndex.messages.uploadSuccessTitle'),
        t('supportIndex.messages.uploadSuccessBody')
      )
      
      // Reset selection
      selectedFiles.value = []
      if (fileInput.value) {
        fileInput.value.value = ''
      }
      
      // Clear result after 5 seconds
      setTimeout(() => {
        uploadResult.value = null
      }, 5000)
    } else {
      uploadResult.value = response.data
      errorStore.setError(
        true,
        response.data.message || t('supportIndex.messages.uploadFailed')
      )
    }
  } catch (err) {
    console.error('Error uploading files:', err)
    errorStore.setError(true, t('supportIndex.messages.uploadException'))
  } finally {
    isUploading.value = false
  }
}

/**
 * Versucht, den Benutzer per Token (Route-Parameter) automatisch einzuloggen
 * – hier als normales Login per Link, nicht als Account-Wechsel.
 */
async function tryLoginWithTokenFromRoute() {
  const token = route.params.token
  if (!token) {
    return
  }

  if (!baseCalls || !userStore) {
    errorStore?.setError?.(true, t('supportIndex.messages.linkLoginInternalError'))
    return
  }

  try {
    loaderStore?.setLoader?.(true, t('supportIndex.messages.linkLoginChecking'))
    const payload = {
      action: 'startlogin',
      token: token
    }

    const response = await baseCalls.userPost(payload)

    if (response.data.success && response.data.user) {
      // User im Store setzen
      userStore.setUser(response.data.user)
    } else {
      const msg = response.data.message || t('supportIndex.messages.linkLoginFailed')
      errorStore?.setError?.(true, msg)
    }
  } catch (err) {
    console.error('Link login error (support):', err)
    errorStore?.setError?.(
      true,
      t('supportIndex.messages.linkLoginException')
    )
  } finally {
    loaderStore?.clearLoader?.()
  }
}

// Watch for route changes
watch(
  () => route.params.id,
  (newId) => {
    if (newId) {
      loadTicket(newId)
      // Reset upload state
      selectedFiles.value = []
      uploadResult.value = null
      if (fileInput.value) {
        fileInput.value.value = ''
      }
    } else {
      loadAllTickets()
    }
  }
)

// Initial Load
onMounted(async () => {
  // Erst versuchen, per Token automatisch einzuloggen (falls vorhanden)
  await tryLoginWithTokenFromRoute()

  if (route.params.id) {
    loadTicket(route.params.id)
  } else {
    loadAllTickets()
  }
})
</script>

<style scoped src="@/assets/styles/sfc/components/sites/SupportIndex.css"></style>
